import React, { useEffect, useMemo, useState } from 'react';
import './TableList.css';
import CoachFormModal from './CoachFormModal';
import LiveBalanceBar from './LiveBalanceBar';
import useLiveBalance from './useLiveBalance';

const toBool = (v) => v === true || v === 1 || v === '1' || v === 'true';

export default function CoachesList({ selectedYear, onYearChange, onListUpdate }) {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('active-first');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);

 const currentYear = new Date().getFullYear();
   const {
     balance,
     loading: balanceLoading,
     prevBalance,
     refresh: refreshBalance
   } = useLiveBalance(selectedYear);
 

  const token = useMemo(() => localStorage.getItem('jwtToken'), []);

  const fetchCoaches = () => {
    setLoading(true);

    if (!token) {
      setError('Token JWT lipsă. Reautentifică-te.');
      setLoading(false);
      return;
    }

    fetch(`https://www.fro.ro/api/coaches/index.php?year=${selectedYear}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`Eroare ${res.status}: ${text}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          const list = data.data || [];
          setCoaches(list);
          setError(null);
          onListUpdate?.(list);
        } else {
          setError(data.message || 'Eroare la încărcarea antrenorilor');
        }
      })
      .catch((err) => {
        console.error('Eroare fetch antrenori:', err);
        setError(err.message || 'Eroare de conexiune');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    onListUpdate?.(coaches);
  }, [coaches, onListUpdate]);

  const handleDelete = (coachId) => {
    if (!window.confirm('Sigur dorești să ștergi acest antrenor?')) return;

    fetch(`https://www.fro.ro/api/coaches/delete.php?id=${coachId}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token },
    })
      .then(async (res) => {
        if (!res.ok) {
          const t = await res.text();
          throw new Error(`HTTP ${res.status}: ${t}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data.success) {
          setCoaches((prev) => prev.filter((r) => r.id !== coachId));
        } else {
          alert('Eroare la ștergere: ' + (data.message || 'necunoscută'));
        }
      })
      .catch((err) => alert('Eroare la conexiune: ' + err.message));
  };

  const handleLegitimare = async (coach) => {
    if (selectedYear < currentYear) {
      alert('Nu poți activa pentru un an anterior.');
      return;
    }
    if (selectedYear > currentYear + 1) {
      alert('Poți activa doar pentru anul curent sau următor.');
      return;
    }

    const confirm = window.confirm(
      `Vrei să activezi antrenorul ${coach.first_name} ${coach.last_name} pentru ${selectedYear}?`
    );
    if (!confirm) return;

    try {
      console.log("Trimitem activare antrenor:", coach.first_name, coach.last_name, "an", selectedYear);
      const response = await fetch(
        'https://www.fro.ro/api/coaches/legitimeaza-coach.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: JSON.stringify({
            coach_id: coach.id,
            club_id: coach.club_id,
            year: selectedYear,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        alert(`Activare efectuată. Taxa: ${data.taxa} lei.`);
        fetchCoaches();
        refreshBalance();
      } else {
        alert('Eroare: ' + (data.message || 'Nu s-a putut activa.'));
      }
    } catch (error) {
      alert('Eroare la conexiune: ' + error.message);
    }
  };

  const handleEdit = (coach) => {
    setEditingCoach(coach);
    setShowAddModal(true);
  };

  const filteredCoaches = coaches.filter((r) =>
    (`${r.first_name || ''} ${r.last_name || ''}`.toLowerCase()).includes(searchTerm.toLowerCase())
  );

  const sortedCoaches = [...filteredCoaches].sort((a, b) => {
    const activeA = toBool(a.is_legitimated);
    const activeB = toBool(b.is_legitimated);

    switch (sortBy) {
      case 'name-az': {
        const nameA = (`${a.last_name || ''} ${a.first_name || ''}`).toLowerCase();
        const nameB = (`${b.last_name || ''} ${b.first_name || ''}`).toLowerCase();
        return nameA.localeCompare(nameB);
      }
      case 'birth-up':
        return (a.birth_year || 0) - (b.birth_year || 0);
      case 'birth-down':
        return (b.birth_year || 0) - (a.birth_year || 0);
      case 'active-first':
        return activeA === activeB ? 0 : activeA ? -1 : 1;
      case 'inactive-first':
        return activeA === activeB ? 0 : activeA ? 1 : -1;
      default:
        return 0;
    }
  });

  if (loading) return <p>Se încarcă antrenorii...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="table-list-container">
      <div className="table-list-header">
         <LiveBalanceBar
                   amount={balance}
                   loading={balanceLoading}
                   prevAmount={prevBalance}
                 />
        <div className="sort-bar">
          <label htmlFor="sort-select" className="sort-label">Sortare:</label>
          <select
            id="sort-select"
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name-az">A–Z după nume</option>
            <option value="birth-up">An naștere ↑</option>
            <option value="birth-down">An naștere ↓</option>
            <option value="active-first">Activi întâi</option>
            <option value="inactive-first">Inactivi întâi</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
          <input
            type="text"
            className="search-bar"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="add-circle-btn"
            onClick={() => {
              setEditingCoach(null);
              setShowAddModal(true);
            }}
            aria-label="Adaugă antrenor"
          >
            <span className="plus-icon">+</span>
          </button>

          {/* ✅ Selector de an: curent + următor */}
          <div className="year-selector">
            <label className="year-label">An:</label>
            <select
              className="year-dropdown"
              value={selectedYear}
              onChange={(e) => onYearChange?.(Number(e.target.value))}
            >
              {[currentYear, currentYear + 1].map((y) => (
                <option key={y} value={y}>
                  {y === currentYear ? `${y} (curent)` : `${y} (următor)`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <table className="table-list">
        <thead>
          <tr>
            <th>Nr. legitimație</th>
            <th>Nume</th>
            <th>An naștere</th>
            <th>Email</th>
            <th>Telefon</th>
            <th>Activat</th>
            <th>Acțiuni</th>
          </tr>
        </thead>
        <tbody>
          {sortedCoaches.length === 0 ? (
            <tr><td colSpan={7}>Nu există antrenori.</td></tr>
          ) : (
            sortedCoaches.map((coach) => (
              <tr
                key={coach.id}
                className={toBool(coach.is_legitimated) ? 'active-row' : ''}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  {coach.license_number ? (
                    <span className="badge-legitimatie">{coach.license_number}</span>
                  ) : (
                    <span className="badge-legitimatie empty">-</span>
                  )}
                </td>
                <td>{coach.first_name} {coach.last_name}</td>
                <td>{coach.birth_year}</td>
                <td>{coach.email || '-'}</td>
                <td>{coach.phone || '-'}</td>

                <td>
                  <input
                    type="checkbox"
                    checked={toBool(coach.is_legitimated)}
                    disabled={toBool(coach.is_legitimated) || selectedYear < currentYear - 1}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleLegitimare(coach);
                    }}
                  />
                </td>

                <td className="table-list-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(coach);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(coach.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {showAddModal && (
        <CoachFormModal
          onClose={() => setShowAddModal(false)}
          onCoachAdded={fetchCoaches}
          editData={editingCoach}
        />
      )}
    </div>
  );
}
